import { PermissionService, RbacActions } from '@supply-mover-2-0/shared';
import React, {
	createContext,
	useContext,
	useEffect,
	useMemo,
	useRef,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { popOutConversation } from '../../components/chat-widget/ChatFloatingWidget';
import { toast } from '../../components/overlays/ToastContainer';
import { notifyIndicator } from '../../notifications/notificationIndicatorStore';
import type {
	ChatMessageView,
	ChatParticipantView,
	ParsedPrincipalId,
} from './chat-types';
import { parsePrincipalId } from './chat-types';
import { ChatQueryService } from './ChatQueryService';
import { useChatConnection } from './useChatConnection';
import { useChatStore } from './useChatStore';

const PARTICIPANTS_REFRESH_DEBOUNCE_MS = 250;

type InboxMessageHandler = (topic: string, payload: unknown) => void;
const noopInboxHandler: InboxMessageHandler = () => undefined;

function resolveMyParticipant(
	principalId: string | null,
	members: ReadonlyArray<ChatParticipantView>,
): ChatParticipantView | undefined {
	const me = parsePrincipalId(principalId);
	if (me == null) return undefined;
	return members.find(
		(p) => p.participantType === me.participantType && p.userId === me.scopeId,
	);
}

function resolveSenderName(
	senderParticipantId: number | undefined,
	members: ReadonlyArray<ChatParticipantView>,
): string | undefined {
	if (senderParticipantId == null) return undefined;
	const p = members.find((m) => m.id === senderParticipantId);
	return p?.displayName;
}

/**
 * Strip any previously-applied "(N) " prefix from the title so we
 * render from a stable base, then reapply a fresh count. Captures
 * once per call so we're resilient to other code that might also
 * prepend to document.title.
 */
function getBaseTitle(): string {
	const raw = typeof document !== 'undefined' ? (document.title ?? '') : '';
	return raw.replace(/^\(\d+\)\s+/, '');
}

/**
 * Lazily request Notification permission on the first user gesture —
 * Chrome/Safari both block `Notification.requestPermission()` calls
 * that aren't gesture-initiated. Registered on the document once;
 * removes itself after the first pointerdown/keydown.
 */
function registerNotificationPermissionOnFirstGesture(): void {
	if (typeof window === 'undefined') return;
	if (typeof Notification === 'undefined') return;
	if (Notification.permission !== 'default') return;
	const request = (): void => {
		document.removeEventListener('pointerdown', request, true);
		document.removeEventListener('keydown', request, true);
		void Notification.requestPermission().catch(() => {
			/* denied/dismissed — we fall back to in-app toasts */
		});
	};
	document.addEventListener('pointerdown', request, true);
	document.addEventListener('keydown', request, true);
}

/**
 * Show a desktop notification if we have permission. Silently no-ops
 * when blocked or unsupported — callers should pair this with an
 * in-app toast fallback, not rely on it for guaranteed delivery.
 */
function showDesktopNotification(opts: {
	title: string;
	body: string;
	tag: string;
	onClick?: () => void;
}): void {
	if (typeof window === 'undefined' || typeof Notification === 'undefined') {
		return;
	}
	if (Notification.permission !== 'granted') return;
	try {
		const n = new Notification(opts.title, {
			body: opts.body,
			// `tag` coalesces notifications for the same conversation
			// so a rapid burst of messages doesn't spam the OS tray.
			// (renotify defaults to false, so repeats stay silent.)
			tag: opts.tag,
		});
		n.onclick = () => {
			window.focus();
			opts.onClick?.();
			n.close();
		};
	} catch {
		/* some browsers throw in private mode — nothing to do */
	}
}

interface ChatContextValue {
	sendText: (
		conversationId: number,
		text: string,
		attachment?: {
			fileId: number;
			fileName: string;
			contentType: string;
		},
	) => Promise<void>;
	sendTyping: (conversationId: number, active: boolean) => Promise<void>;
	sendRead: (conversationId: number, messageId: number) => Promise<void>;
	/**
	 * Retry sending an optimistic message that previously failed (by its
	 * clientMessageId). Looks up the stored text and re-publishes.
	 */
	retrySend: (conversationId: number, clientMessageId: string) => Promise<void>;
	/**
	 * After creating a new conversation via REST, surface it locally so
	 * the channel list + detail pane render it without waiting for the
	 * next full refetch. Also loads members into the store.
	 */
	loadConversationInto: (conversationId: number) => Promise<void>;
	/**
	 * Manual reconnect. Only meaningful when the connection has
	 * landed in `failed` (terminal auth rejection or hit the
	 * consecutive-failure cap). Triggers a fresh connect attempt
	 * with the current credentials. Use this from a "Try again"
	 * button surfaced alongside the Disconnected banner.
	 */
	retryConnection: () => void;
}

const ChatContext = createContext<ChatContextValue | null>(null);

/**
 * Owns the MQTT connection + wires inbox messages into the Zustand
 * store. Surfaces a small imperative API (sendText/sendTyping/sendRead/
 * loadConversationInto) for page components to call.
 */
export const ChatProvider: React.FC<{
	authToken: string;
	/**
	 * Force a token refresh and resolve with the fresh bearer token.
	 * Passed straight through to useChatConnection so a broker auth
	 * rejection recovers the same way an HTTP 401 does. Supplied by
	 * AuthenticatedApp, which owns the AuthService instance — the chat
	 * layer stays auth-source-agnostic.
	 */
	refreshToken: () => Promise<string | null>;
	children: React.ReactNode;
}> = ({ authToken, refreshToken, children }) => {
	// Discrete selectors — destructuring the whole store re-renders
	// the provider on EVERY store mutation (every message, typing
	// blip, unread bump). With ~hundreds of mutations per active
	// session, that's a meaningful waste. Selectors only re-render
	// when their specific value changes; setter references from
	// Zustand are stable across renders.
	const principalId = useChatStore((s) => s.principalId);
	const brokerUrl = useChatStore((s) => s.brokerUrl);
	const inboxTopic = useChatStore((s) => s.inboxTopic);
	const outboxTopic = useChatStore((s) => s.outboxTopic);
	const presenceTopic = useChatStore((s) => s.presenceTopic);
	const queueEventsTopic = useChatStore((s) => s.queueEventsTopic);
	const availabilityEventsTopic = useChatStore(
		(s) => s.availabilityEventsTopic,
	);
	const setConnected = useChatStore((s) => s.setConnected);
	const appendMessage = useChatStore((s) => s.appendMessage);
	const appendMessages = useChatStore((s) => s.appendMessages);
	const setTyping = useChatStore((s) => s.setTyping);
	const upsertConversation = useChatStore((s) => s.upsertConversation);
	const setParticipants = useChatStore((s) => s.setParticipants);
	const incrementUnread = useChatStore((s) => s.incrementUnread);
	// SPA navigation for toast/notification onClick handlers — using
	// window.location.assign would force a full-page reload, which
	// tears down the mqtt connection, drops typing state, and
	// re-bootstraps the store. navigate() does a router-level
	// transition so the existing chat session keeps running.
	const navigate = useNavigate();
	// `setIdentity`, `setConversations`, `setUnreadMap` are only used
	// inside the bootstrap effect — that effect reads them via
	// `useChatStore.getState()` so the deps array can stay
	// `[hasChatAccess]` and not re-fire on store mutations. No
	// standalone selectors needed.

	// Arm a one-shot listener for Notification permission so we can
	// prompt on the user's first interaction rather than on mount
	// (which browsers refuse to honor).
	useEffect(() => {
		registerNotificationPermissionOnFirstGesture();
	}, []);

	// Tab-title unread indicator: prepend "(N) " when there are
	// unread messages, restore the original on clean-up / no unread.
	// Muted conversations are excluded to keep the signal honest.
	const unreadByConversationId = useChatStore((s) => s.unreadByConversationId);
	const participantsByConversationForTitle = useChatStore(
		(s) => s.participantsByConversation,
	);
	useEffect(() => {
		if (typeof document === 'undefined') return;
		const base = getBaseTitle();
		const me = parsePrincipalId(principalId);
		if (me == null) {
			document.title = base;
			return;
		}
		let total = 0;
		for (const [convoIdStr, unread] of Object.entries(unreadByConversationId)) {
			if (unread.unreadCount <= 0) continue;
			const convoId = Number(convoIdStr);
			const members = participantsByConversationForTitle[convoId] ?? [];
			const myMember = members.find((p) => p.userId === me.scopeId);
			if (Boolean(myMember?.notificationsMuted)) continue;
			total += unread.unreadCount;
		}
		document.title = total > 0 ? `(${total}) ${base}` : base;
	}, [unreadByConversationId, participantsByConversationForTitle, principalId]);

	// Users without chat:read get NO bootstrap — the provider stays
	// inert (no principalId, no broker connection, no nav dot). Any
	// component that tries to call sendText / loadConversationInto
	// still works through the API surface but will short-circuit on
	// the principalId null check. Avoids the 403 spam + broken UI
	// that otherwise results from auto-loading chat for unauthorized
	// users.
	const hasChatAccess = PermissionService.can(RbacActions.CHAT.READ);

	// Bootstrap identity + initial conversations on mount.
	//
	// Parallel fetches: identity and listConversations are
	// independent. Sequential they costed two RTTs; parallel costs
	// one. Failure is retried with exponential backoff capped at 30s
	// — a transient network blip on first paint would otherwise
	// leave the user stuck without chat until they reload.
	useEffect(() => {
		if (!hasChatAccess) return;
		let cancelled = false;
		let attempt = 0;
		let retryHandle: number | null = null;

		const run = async (): Promise<void> => {
			try {
				const [identity, listResult] = await Promise.all([
					ChatQueryService.getIdentity(),
					ChatQueryService.listConversations(),
				]);
				if (cancelled) return;
				// Setters read via getState() inside the effect so
				// the effect's deps array can be just `[hasChatAccess]`.
				const store = useChatStore.getState();
				store.setIdentity({
					principalId: identity.principalId,
					brokerUrl: identity.brokerUrl,
					inboxTopic: identity.inboxTopic,
					outboxTopic: identity.outboxTopic,
					presenceTopic: identity.presenceTopic,
					queueEventsTopic: identity.queueEventsTopic ?? null,
					availabilityEventsTopic: identity.availabilityEventsTopic ?? null,
				});
				store.setConversations(listResult.conversations);
				// Batch participant sets via a single set() call —
				// 50 sequential dispatches would otherwise trigger 50
				// re-renders for any selector reading
				// participantsByConversation.
				const nextParticipants: Record<number, Array<ChatParticipantView>> = {};
				for (const [convoId, members] of Object.entries(
					listResult.membersByConversationId,
				)) {
					nextParticipants[Number(convoId)] = members;
				}
				useChatStore.setState((state) => ({
					participantsByConversation: {
						...state.participantsByConversation,
						...nextParticipants,
					},
				}));
				store.setUnreadMap(listResult.unreadByConversationId);
			} catch (err) {
				if (cancelled) return;
				console.error('[ChatProvider] bootstrap failed', err);
				// Exponential backoff: 1s, 2s, 4s, 8s, 16s, then 30s
				// cap. Cancellable via cleanup.
				attempt += 1;
				const delayMs = Math.min(30_000, 1_000 * 2 ** (attempt - 1));
				retryHandle = window.setTimeout(() => {
					retryHandle = null;
					void run();
				}, delayMs);
			}
		};

		void run();
		return () => {
			cancelled = true;
			if (retryHandle != null) window.clearTimeout(retryHandle);
		};
	}, [hasChatAccess]);

	const connectionConfig = useMemo(() => {
		// Empty `authToken` is the placeholder AuthenticatedApp passes
		// during a transient token-refresh window — if we tried to
		// connect to mqtt with no password the broker would reject and
		// mqtt.js would reconnect-loop. Wait for a real token instead.
		if (
			principalId == null ||
			brokerUrl == null ||
			authToken == null ||
			authToken === ''
		) {
			return null;
		}
		return {
			wsUrl: brokerUrl,
			principalId,
			token: authToken,
			refreshToken,
			// Presence topic drives the Last Will (offline-on-disconnect)
			// the connection registers — server-built, arrives via identity.
			presenceTopic: presenceTopic ?? undefined,
		};
	}, [principalId, brokerUrl, authToken, refreshToken, presenceTopic]);

	const chat = useChatConnection(connectionConfig);
	const setConnectionState = useChatStore((s) => s.setConnectionState);

	useEffect(() => {
		setConnected(chat.connected);
	}, [chat.connected, setConnected]);

	// Mirror the richer connection-state machine into the store so
	// the page-level banner can differentiate "Reconnecting…"
	// (transient) from "Disconnected" (we gave up after N consecutive
	// failures or hit a terminal auth rejection — the user needs to
	// retry manually). `chat.failureReason` is null in any non-failed
	// state; we pass it through so the banner can pick the right
	// copy.
	useEffect(() => {
		setConnectionState(chat.state, chat.failureReason);
	}, [chat.state, chat.failureReason, setConnectionState]);

	// Per-conversation debounce timers for participants-changed
	// events. A burst of membership mutations (e.g. an admin
	// promoting/demoting several users in quick succession) would
	// otherwise fire one listMembers call per event; trailing
	// debounce coalesces them into a single fetch per conversation.
	const participantsRefreshTimersRef = useRef<Map<number, number>>(new Map());
	useEffect(() => {
		const timers = participantsRefreshTimersRef.current;
		return () => {
			for (const id of timers.values()) window.clearTimeout(id);
			timers.clear();
		};
	}, []);

	// Inbox handler kept in a ref so the subscribe effect's deps can
	// be just `[chat.connected, principalId]`. Without this, every
	// reconnect (chat reference identity changes when `connected`
	// flips) would trigger an unsub/resub round-trip — wasted JS work
	// and a subtle window where we could miss a message.
	const inboxHandlerRef = useRef<InboxMessageHandler>(noopInboxHandler);
	// REASSIGNED ON EVERY RENDER on purpose — the closure below
	// captures the latest `appendMessage`, `incrementUnread`, etc.
	// without re-subscribing the broker handler. Don't move into
	// useEffect; that would defeat the staleness fix.
	inboxHandlerRef.current = (_topic, payload) => {
		const p = payload as Record<string, unknown>;
		if (p == null || typeof p !== 'object') return;
		switch (p['type']) {
			case 'message': {
				const conversationId = p['conversationId'] as number;
				const fkFileId = p['fkFileId'] as number | undefined;
				// `kind` MUST come from the server — system messages
				// have no senderParticipantId and `kind: 'system'`,
				// and the client renders them as centered pills based
				// on that field. Deriving from fkFileId would render
				// system messages as text bubbles. Fall back to a
				// derived value only if the server somehow omits it
				// (legacy clients / older fanout payloads).
				const serverKind = p['kind'] as ChatMessageView['kind'] | undefined;
				const msg: ChatMessageView = {
					id: p['messageId'] as number,
					uuid: p['messageUuid'] as string,
					conversationId,
					senderParticipantId: p['senderParticipantId'] as number | undefined,
					senderWorkdUserId: p['senderWorkdUserId'] as number | undefined,
					clientMessageId: p['clientMessageId'] as string | undefined,
					kind: serverKind ?? (fkFileId != null ? 'file' : 'text'),
					content: p['text'] as string,
					createdDate: new Date(
						(p['sentAt'] as number) ?? Date.now(),
					).toISOString(),
					fkFileId,
					fileName: (p['fileName'] as string | null) ?? undefined,
					fileContentType: (p['fileContentType'] as string | null) ?? undefined,
				};
				appendMessage(conversationId, msg);

				const state = useChatStore.getState();
				const myPrincipalId = state.principalId;
				const fromPrincipalId = p['fromPrincipalId'] as string | undefined;
				const isFromMe =
					fromPrincipalId != null && fromPrincipalId === myPrincipalId;
				// "Active" means the user is BOTH currently on the
				// chat surface AND has this conversation selected.
				// activeConversationId persists in the store across
				// route changes (intentional — so re-entering /chat
				// resumes the last selection), so we additionally
				// gate on the URL. Without this URL gate, claiming a
				// chat then navigating to another page would silence
				// every subsequent message — the rep would never see
				// an unread bump in the sidebar OR the nav badge.
				const isOnChatPage =
					typeof window !== 'undefined' &&
					window.location.pathname.startsWith('/chat');
				const isActive =
					isOnChatPage && state.activeConversationId === conversationId;
				const members = state.participantsByConversation[conversationId] ?? [];
				const myMember = resolveMyParticipant(myPrincipalId, members);
				const isMuted = myMember?.notificationsMuted ?? false;
				// Server-computed per-recipient mention flag (PD-14294). When set,
				// the @-mention bell (driven by the realtime 'notification'
				// envelope below) is the SOLE alert — we suppress the duplicate
				// chat toast/desktop notification so a mention isn't double-alerted.
				const mentionsMe = Boolean(p['mentionsMe']);

				// Only count unread + toast if we're actually a
				// participant in this conversation. Server-side
				// fanout already enforces this (only delivers to
				// participant inboxes), but a future bug or a
				// stale-cleanup edge could deliver an event for a
				// conversation we've left. Belt and suspenders.
				// System messages (`kind === 'system'`) are server-emitted
				// chrome — "Connected with <Rep>", "Customer ended the
				// chat", "Sales rep ended the chat", etc. They aren't
				// engagement events from another user, so they
				// shouldn't bump unread or fire toasts/desktop
				// notifications. The catch-up path already skips them
				// (`if (m.kind === 'system') continue;`); this is the
				// matching live-path guard.
				const isSystemMessage = msg.kind === 'system';

				if (!isFromMe && !isActive && myMember != null && !isSystemMessage) {
					incrementUnread(conversationId, /* isMention */ false);
					// A mention shows ONLY the bell (its realtime 'notification'
					// nudge), not a duplicate chat toast/desktop. Non-mention
					// messages keep the existing toast/desktop behavior.
					// NOTE (PD-14298 review): `mentionsMe` is set server-side from
					// the mention list alone, independent of the recipient's in-app
					// MENTION preference. A user who turns the in-app mention bell
					// OFF therefore gets neither the bell nor this toast. That's
					// arguably intended ("no in-app mention alerts"), and making
					// the toast a fallback would require resolving the per-user
					// pref on the chat message hot path — deferred pending a
					// product call. See the ADR.
					if (!isMuted && !mentionsMe) {
						const senderName = resolveSenderName(
							p['senderParticipantId'] as number | undefined,
							members,
						);
						const convo = state.conversations[conversationId];
						const convoLabel = convo?.name ?? senderName ?? 'Chat';
						const previewRaw = (p['text'] as string) ?? '';
						const preview =
							previewRaw.length > 80
								? `${previewRaw.slice(0, 77)}…`
								: previewRaw;
						const title =
							convo != null && convo.channel !== 'dm'
								? `${senderName ?? 'Someone'} in #${convoLabel}`
								: (senderName ?? convoLabel);
						if (typeof document !== 'undefined' && document.hidden) {
							showDesktopNotification({
								title,
								body: preview,
								tag: `chat-convo-${conversationId}`,
								onClick: () => popOutConversation(conversationId),
							});
						} else {
							toast.action(`${title}\n${preview}`, {
								duration: 6000,
								actionLabel: 'Open',
								onClick: () => {
									popOutConversation(conversationId);
								},
							});
						}
					}
				}

				// Bump lastMessageAt locally so the sidebar reorders
				// the conversation to the top. We deliberately do
				// NOT refetch the conversation list here — at one
				// REST call per inbound message that's a serious
				// performance trap on a busy session. The single
				// authoritative copy of the new lastMessageAt is
				// the message we just received; everything else
				// (latestMessageId, messageCount) updates locally
				// next paint via the message store.
				const existing = state.conversations[conversationId];
				if (existing != null) {
					const sentAt = (p['sentAt'] as number) ?? Date.now();
					const sentAtIso = new Date(sentAt).toISOString();
					// Skip the upsert when the new timestamp isn't
					// actually newer — defensive against
					// out-of-order delivery from the broker.
					if (
						existing.lastMessageAt == null ||
						existing.lastMessageAt < sentAtIso
					) {
						upsertConversation({
							...existing,
							lastMessageAt: sentAtIso,
							latestMessageId: msg.id,
							messageCount: existing.messageCount + 1,
						});
					}
				} else {
					// Conversation is in the user's inbox but not yet
					// in the local store. Two known cases produce
					// this:
					//   1. Auto-claim: server added the rep as a
					//      participant on a fresh customer-support
					//      conversation, fanned out the "Connected
					//      with X" system message to inboxes, but
					//      the rep had bootstrapped their conversation
					//      list BEFORE this conversation existed —
					//      so it isn't in `conversations[]`.
					//   2. Mid-session add to an existing group: a
					//      teammate added the rep to a group; the
					//      next message in that group lands in the
					//      rep's inbox before any client signal said
					//      "you're now in this group."
					//
					// Either way, the right move is to fetch the
					// conversation now. `loadConversationInto`
					// populates the conversation row + participants
					// in the store, after which the sidebar renders
					// it normally and subsequent messages flow
					// through the `existing != null` branch above.
					//
					// No throttling/de-duping: in steady state, this
					// only fires on the very first inbox event for
					// each new conversation. Cost is bounded by
					// "rate of new conversations the rep gets added
					// to," which is small.
					void loadConversationInto(conversationId);
				}
				break;
			}
			case 'typing': {
				setTyping(
					p['conversationId'] as number,
					p['fromPrincipalId'] as string,
					Boolean(p['active']),
				);
				break;
			}
			case 'read': {
				// TODO: render read markers
				break;
			}
			case 'participants-changed': {
				const conversationId = p['conversationId'] as number;
				if (conversationId == null) break;
				// Trailing debounce: coalesce a burst of membership
				// shifts on the same conversation into a single
				// REST refetch.
				const timers = participantsRefreshTimersRef.current;
				const existing = timers.get(conversationId);
				if (existing != null) window.clearTimeout(existing);
				const handle = window.setTimeout(() => {
					timers.delete(conversationId);
					void ChatQueryService.listMembers(conversationId)
						.then((members) => setParticipants(conversationId, members))
						.catch(() => undefined);
				}, PARTICIPANTS_REFRESH_DEBOUNCE_MS);
				timers.set(conversationId, handle);
				break;
			}
			case 'notification': {
				// Realtime bell nudge (PD-14294). A NUDGE only — REST is the
				// source of truth, so we re-pull the authoritative unread count
				// (and the list if the tray is open) via the registered bell
				// indicator rather than incrementing locally (which would
				// double-count against the 60s/30s poll fallback). No-op when
				// no bell is mounted.
				notifyIndicator();
				break;
			}
			default:
				break;
		}
	};

	useEffect(() => {
		if (!chat.connected || principalId == null || inboxTopic == null) {
			return;
		}
		const unsub = chat.subscribe(inboxTopic, (topic, payload) =>
			inboxHandlerRef.current(topic, payload),
		);
		return unsub;
		// `chat` (the connection object) is intentionally excluded —
		// its identity flips on every connect/disconnect, which would
		// trigger a wasteful unsub/resub round-trip while the broker
		// already re-subscribes on connect via the hook's own logic.
		// We DO want to resubscribe on principalId / inboxTopic change
		// (both rare) and on connected→true (so the JS-side handler is
		// registered before any inbox event lands).
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [chat.connected, principalId, inboxTopic]);

	// Bootstrap the caller's `canReceiveCustomerChats` flag. Drives
	// queue chrome visibility (pane, MQTT subscription, nav link).
	// Single fetch on connect — the flag is admin-set so it doesn't
	// change at runtime from the rep's own activity. If an admin
	// flips it while the rep is online, the rep sees the change on
	// next page reload — acceptable trade-off for not maintaining
	// a live subscription on a rare event.
	useEffect(() => {
		if (!chat.connected || principalId == null) return;
		let cancelled = false;
		void (async () => {
			try {
				const r = await ChatQueryService.getMyAvailability();
				if (cancelled) return;
				useChatStore
					.getState()
					.setMyCanReceiveCustomerChats(r.canReceiveCustomerChats);
			} catch {
				// Network blip / 403 — leave as null so queue chrome
				// stays hidden rather than flashing visible. Next
				// reconnect re-runs this fetch.
				if (!cancelled) {
					useChatStore.getState().setMyCanReceiveCustomerChats(null);
				}
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [chat.connected, principalId]);

	/**
	 * Presence heartbeat — publishes to `chat/presence/{principalId}`
	 * every PRESENCE_PUBLISH_MS while the user has shown browser
	 * activity within IDLE_THRESHOLD_MS. Server subscriber writes a
	 * Redis key with 60s TTL on each receipt; the AvailabilityService
	 * composition rule consumes that to compose effective status.
	 *
	 * Why activity-aware: a tab that's been open for 3 days while
	 * the user is gone shouldn't read as `available`. We track
	 * recent mouse / key / scroll / focus / tab-visibility events
	 * — any one resets the clock. After IDLE_THRESHOLD_MS without
	 * any of those, the heartbeat stops, the Redis key TTLs out
	 * within ~60s, and effective status flips to `away` for
	 * teammates and routing. When activity resumes, the next 30s
	 * tick publishes again and the user is back online within ~30s.
	 *
	 * Key conventions:
	 *   - 60s heartbeat × 120s Redis TTL = 2× safety margin against
	 *     a single dropped publish. Halved from 30s: the Last Will now
	 *     clears presence on disconnect, so we no longer need a fast
	 *     heartbeat to notice an absence — and this cuts the per-user
	 *     IoT presence message volume in half.
	 *   - 5min idle threshold matches Teams (Slack uses 10).
	 *   - Activity listeners attach to window, NOT specific elements
	 *     — the rep can be heads-down in the orders module all day
	 *     and still register as active.
	 *
	 * Internal-only: portal-side presence is wired separately by the
	 * storefront's HelpChatWidget if/when desired. This effect runs
	 * for any chat principal; portal grants don't include the
	 * presence topic, so a portal client publish would just no-op
	 * at the broker. Harmless either way.
	 */
	useEffect(() => {
		if (!chat.connected || principalId == null || presenceTopic == null) {
			return;
		}

		const PRESENCE_PUBLISH_MS = 60_000;
		const IDLE_THRESHOLD_MS = 5 * 60_000;
		// presenceTopic is server-built (ChatTopicService) so the env
		// segment is correct in one place. Don't reconstruct it here.
		// Initialize as "active right now" — the user just opened
		// the page, that's an interaction. Without this seed, the
		// first 30s tick would skip publishing because lastActivityAt
		// would be 0 and the (now - lastActivity) check would be
		// huge.
		let lastActivityAt = Date.now();

		const onActivity = (): void => {
			lastActivityAt = Date.now();
		};
		const onVisibility = (): void => {
			if (document.visibilityState === 'visible') {
				lastActivityAt = Date.now();
			}
		};

		// Passive listeners — never call preventDefault, never block
		// scrolling. mousemove fires constantly; assignment-only
		// handler is cheap. We don't need to throttle.
		window.addEventListener('mousemove', onActivity, { passive: true });
		window.addEventListener('keydown', onActivity, { passive: true });
		window.addEventListener('click', onActivity, { passive: true });
		window.addEventListener('scroll', onActivity, { passive: true });
		window.addEventListener('focus', onActivity);
		document.addEventListener('visibilitychange', onVisibility);

		const publish = (): void => {
			void chat.publish(presenceTopic, { at: Date.now() }).catch(() => {
				// Broker hiccup or auth blip. The next tick will
				// retry; meanwhile the existing Redis key keeps the
				// user alive until its TTL expires. No need to alarm.
			});
		};

		// Publish immediately on connect so the user is online ASAP
		// rather than waiting up to 30s for the first tick.
		publish();

		const id = setInterval(() => {
			if (Date.now() - lastActivityAt < IDLE_THRESHOLD_MS) {
				publish();
			}
			// Else: skip. Redis TTL takes over → effective status
			// composes to `away` until activity resumes.
		}, PRESENCE_PUBLISH_MS);

		return () => {
			clearInterval(id);
			window.removeEventListener('mousemove', onActivity);
			window.removeEventListener('keydown', onActivity);
			window.removeEventListener('click', onActivity);
			window.removeEventListener('scroll', onActivity);
			window.removeEventListener('focus', onActivity);
			document.removeEventListener('visibilitychange', onVisibility);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [chat.connected, principalId, presenceTopic]);

	// Tenant-scoped queue events. Internal users get a tiny "queue
	// changed" ping when an enqueue/claim/abandon/resolve happens
	// anywhere in the tenant; we then refetch /chat/queue to update
	// the rep's claimable list. This replaces the 15s poll
	// ChatNavIndicator used to do — same UX, no idle traffic.
	//
	// The subscribe is conditional: only internal principals (queue
	// is rep-side, portal users have no queue concept), and only for
	// users with `canReceiveCustomerChats=true` — the per-user flag
	// is the single source of truth for queue visibility now (RBAC
	// actions chat:queue:view / chat:queue:claim were retired in
	// favor of this flag to avoid the "two locks on one door"
	// confusion). The flag arrives in the store via the bootstrap
	// fetch below; until it does, the gate stays closed (null
	// treated as "not yet known, hide UI").
	const me = useMemo<ParsedPrincipalId | null>(
		() => parsePrincipalId(principalId),
		[principalId],
	);
	const tenantAliasFromPrincipal =
		me?.type === 'internal' ? me.tenantAlias : null;
	const myCanReceiveCustomerChats = useChatStore(
		(s) => s.myCanReceiveCustomerChats,
	);
	const canViewQueue = myCanReceiveCustomerChats === true;
	// The rep's instanceUserId — drives the "MY customer just queued"
	// detection. Internal principalId shape is
	// `internal:{tenant}:{workdUserId}:{instanceUserId}`; segs[3] is
	// the instance id. Client identifies their assigned rep by
	// instance id (server-side `client.fkUserId`).
	// In a tenant DB, `fk_user__id`-style FKs reference `_user._id`
	// = instanceUserId. `client.fkUserId` (the customer's assigned
	// rep) is therefore an instanceUserId, and the queue's
	// `requestedRepUserId` carries that same value. So "is this MY
	// customer?" is a straight equality against my instanceUserId.
	// (The audit fields like `_creator_userId` are the only place
	// workdUserIds appear in tenant DBs; chat queue routing is
	// nowhere near those.)
	const myInstanceUserId = me?.instanceUserId ?? null;

	useEffect(() => {
		if (!chat.connected || tenantAliasFromPrincipal == null) return;
		if (!canViewQueue) return;
		const handler = (_topic: string, payload: unknown): void => {
			const p =
				payload != null && typeof payload === 'object'
					? (payload as Record<string, unknown>)
					: null;

			// Diagnostic: surfaces "queue event arrived" without
			// ambiguity. console.debug is the project convention for
			// debug-only logging (CLAUDE.md) — visible when the
			// browser's debug log level is enabled, silent otherwise.
			if (p != null) {
				console.debug(
					'[ChatProvider] queue event',
					p['type'],
					'requestedRepUserId=',
					p['requestedRepUserId'],
					'myInstanceUserId=',
					myInstanceUserId,
				);
			}

			// Toast + desktop-notify when MY assigned customer just
			// queued. Fires BEFORE the REST refetch so the alert
			// lands as soon as the broker delivers, not after the
			// round-trip.
			//
			// ADDITIVE — always show the in-app toast (it persists in
			// the tab so the rep sees it when they come back), and
			// ALSO fire a desktop notification when the tab is
			// backgrounded. The previous "either/or" logic meant a
			// rep on a hidden tab with Notification permission still
			// in `default` got nothing at all.
			if (p != null && p['type'] === 'enqueued') {
				const openQueue = (): void => {
					// Use react-router so we don't full-reload the SPA
					// (lose chat connection, re-bootstrap the store,
					// re-fetch everything). navigate() does an SPA
					// transition; ChatPage mounts fresh and picks up
					// the existing connection from this provider.
					navigate('/chat');
				};
				const isForMe =
					typeof p['requestedRepUserId'] === 'number' &&
					myInstanceUserId != null &&
					p['requestedRepUserId'] === myInstanceUserId;
				if (isForMe) {
					// Critical alert — your specific customer.
					// Toast + (when hidden) a desktop notification.
					const title = 'Your customer is waiting';
					const body =
						'A customer who asked for you specifically just opened a chat.';
					toast.action(`${title}\n${body}`, {
						duration: 8000,
						actionLabel: 'Open queue',
						onClick: openQueue,
					});
					if (typeof document !== 'undefined' && document.hidden) {
						showDesktopNotification({
							title,
							body,
							tag: 'chat-queue-mine',
							onClick: openQueue,
						});
					}
				} else {
					// Soft toast for general queue activity. Shorter
					// duration, no desktop notification — this isn't
					// urgent for the rep, just an awareness ping that
					// the queue grew. Lets a rep with an empty plate
					// see "someone is waiting" without missing it,
					// and doesn't interrupt a rep already busy with
					// another conversation.
					toast.action('A customer is waiting in the queue', {
						duration: 4000,
						actionLabel: 'Open queue',
						onClick: openQueue,
					});
				}
			}
			ChatQueryService.listClaimableQueue()
				.then((rows) => {
					console.debug('[ChatProvider] queue refetch ok rows=', rows.length);
					useChatStore.getState().setQueueEntries(rows);
				})
				.catch((err) => {
					console.debug('[ChatProvider] queue refetch failed', err);
					/* 403 (lost permission) or transient network — ignore */
				});
		};
		// Prime the cache on subscribe so a freshly-mounted session
		// sees the current queue without waiting for the next event.
		handler('', null);
		// Topic comes from the identity response — server-built so the
		// env segment is authoritative. If it's null (portal principal,
		// or identity hasn't loaded yet) we don't subscribe.
		if (queueEventsTopic == null) return;
		const unsub = chat.subscribe(queueEventsTopic, handler);
		console.debug(
			'[ChatProvider] subscribed to queue events',
			queueEventsTopic,
		);
		return unsub;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		chat.connected,
		queueEventsTopic,
		canViewQueue,
		myInstanceUserId,
		navigate,
	]);

	/**
	 * Live availability subscription — internal users only.
	 *
	 * Replaces what would otherwise be per-component polling of
	 * `/api/chat/availability` with a single MQTT subscription on
	 * `chat/availability/{tenant}/events`. Every status change on
	 * the server publishes one envelope; we apply it directly to
	 * the store and every consumer (DM rows, member drawer, the
	 * status switcher itself) re-renders without a refetch.
	 *
	 * Hydration: on subscribe we do ONE bulk REST fetch of every
	 * userId we already track (`participantsByConversation`). The
	 * subscription is the only update path after that — no
	 * setInterval, no per-component fetches.
	 *
	 * Privacy guard: the broker only grants subscribe access to
	 * this topic for internal principals (see `chat-topic.service`
	 * `grantsForPrincipal`). Portal principals never receive these
	 * events; their `/portal/chat/context` already returns the
	 * tenant-aggregate `teamAvailable` boolean, so they don't need
	 * per-rep events anyway.
	 */
	useEffect(() => {
		if (!chat.connected || availabilityEventsTopic == null) return;
		if (me?.type !== 'internal') return;

		// Topic is server-built (env-scoped) and arrives via the identity
		// response. The client never composes it locally — see the
		// store comment for the full rationale.
		const topic = availabilityEventsTopic;

		// Bulk hydration. Pulls userIds from the participants index
		// (every user we currently track in any conversation), then
		// REST-fetches their statuses in one round trip. Limited to
		// 200 ids by the controller — power users with massive
		// participant graphs get truncated, but the subscription
		// fills in stragglers as their statuses change.
		const hydrate = async (): Promise<void> => {
			const state = useChatStore.getState();
			const ids = new Set<number>();
			for (const members of Object.values(state.participantsByConversation)) {
				for (const p of members) {
					if (p.userId != null) ids.add(p.userId);
				}
			}
			const list = Array.from(ids);
			if (list.length === 0) return;
			try {
				const rows = await ChatQueryService.getAvailabilityForUsers(list);
				const map: Record<
					number,
					{ status: 'available' | 'away' | 'offline'; message: string | null }
				> = {};
				for (const r of rows) {
					map[r.userId] = { status: r.status, message: r.message };
				}
				useChatStore.getState().setAvailabilityMap(map);
			} catch (err) {
				console.debug('[ChatProvider] availability hydrate failed', err);
				/* transient — subscription updates fill the gap */
			}
		};

		void hydrate();

		const handler = (_topic: string, payload: unknown): void => {
			const p =
				payload != null && typeof payload === 'object'
					? (payload as Record<string, unknown>)
					: null;
			if (p == null) return;
			if (p['type'] !== 'availability-changed') return;
			const userId =
				typeof p['userId'] === 'number' ? p['userId'] : Number(p['userId']);
			const status = p['status'] as 'available' | 'away' | 'offline';
			if (!Number.isFinite(userId) || userId <= 0) return;
			if (status !== 'available' && status !== 'away' && status !== 'offline') {
				return;
			}
			const message =
				typeof p['message'] === 'string' && p['message'].length > 0
					? p['message']
					: null;
			useChatStore.getState().applyAvailabilityChange(userId, status, message);
		};

		const unsub = chat.subscribe(topic, handler);
		console.debug('[ChatProvider] subscribed to availability events', topic);
		return unsub;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [chat.connected, availabilityEventsTopic, me?.type]);

	// Catch-up on (re)connect. Whenever the broker comes back online
	// (initial connect or post-drop reconnect) we ask the server for
	// any messages newer than what the store currently knows about,
	// per conversation. Without this, a brief network blip would
	// silently swallow messages the broker delivered while we were
	// offline (clean=true sessions don't queue).
	//
	// Concurrency-capped: a power user with 50 cached conversations
	// would otherwise fire 50 parallel REST calls on every reconnect,
	// stampeding the server every time their wifi flickers. Cap at
	// MAX_PARALLEL so the worst-case load scales with rep count, not
	// rep count × cached convo count.
	//
	// Also refetch participants for every conversation we catch up
	// on. While we were offline, a `participants-changed` event may
	// have been published; clean=true sessions don't queue, so we
	// would otherwise stay forever stale on whatever membership
	// changed during the gap.
	useEffect(() => {
		if (!chat.connected) return;
		const state = useChatStore.getState();
		const conversationIds = Object.keys(state.messagesByConversation).map((k) =>
			Number(k),
		);
		const work: Array<{ conversationId: number; latest: number }> = [];
		for (const conversationId of conversationIds) {
			const msgs = state.messagesByConversation[conversationId] ?? [];
			const latest = msgs.reduce((acc, m) => (m.id > acc ? m.id : acc), 0);
			if (latest <= 0) continue;
			work.push({ conversationId, latest });
		}
		if (work.length === 0) return;

		const MAX_PARALLEL = 6;
		let cancelled = false;
		let cursor = 0;
		const runOne = async (): Promise<void> => {
			while (!cancelled) {
				const idx = cursor++;
				if (idx >= work.length) return;
				const item = work[idx];
				if (item == null) return;
				const { conversationId, latest } = item;
				try {
					const [newer, members] = await Promise.all([
						ChatQueryService.listMessages(conversationId, {
							afterMessageId: latest,
							limit: 200,
						}),
						ChatQueryService.listMembers(conversationId),
					]);
					// Re-check cancelled AFTER the awaits — if the
					// connection dropped + a fresh catch-up effect
					// started while we were in flight, our results
					// are stale and would overwrite the newer fetch's
					// participants/messages with older data.
					if (cancelled) return;
					// Bulk insert — sorts once and mutates the store
					// once, vs. N×O(N log N) for appendMessage in a
					// loop with a 200-msg backfill.
					appendMessages(conversationId, newer);
					setParticipants(conversationId, members);

					// Bump unread for catch-up messages. Without this
					// the rep would never see "you missed messages"
					// after a reconnect — the dot only fires for
					// messages that arrive live on the inbox topic.
					// Same gate as the live handler: skip if from-me
					// or if currently viewing this conversation on
					// /chat. Mute is honored downstream by the
					// indicator/sidebar, not here, so we always bump
					// the counter (clears when the rep opens it).
					const liveState = useChatStore.getState();
					const myMember = members.find(
						(p) =>
							me != null &&
							p.participantType === me.participantType &&
							p.userId === me.scopeId,
					);
					if (myMember == null) continue;
					const isOnChatPage =
						typeof window !== 'undefined' &&
						window.location.pathname.startsWith('/chat');
					const isViewingThis =
						isOnChatPage && liveState.activeConversationId === conversationId;
					if (isViewingThis) continue;
					const myParticipantId = myMember.id;
					for (const m of newer) {
						if (m.kind === 'system') continue;
						if (m.senderParticipantId === myParticipantId) continue;
						incrementUnread(conversationId, /* isMention */ false);
					}
				} catch {
					/* per-convo failure is isolated; next reconnect retries */
				}
			}
		};
		const workers = Array.from({
			length: Math.min(MAX_PARALLEL, work.length),
		}).map(() => runOne());
		void Promise.all(workers);
		return () => {
			cancelled = true;
		};
	}, [chat.connected, appendMessages, setParticipants, incrementUnread, me]);

	// Mark-as-read side-effect: when the user focuses a conversation,
	// zero out the local unread count AND send a `read` envelope on
	// the outbox so the server clears chat_read_state. We key on the
	// active conversation id plus the highest message id we currently
	// know about — that's what the server stores as
	// `lastReadMessageId`.
	//
	// We keep a per-conversation watermark of the last messageId we
	// already published a `read` for, so that connect/disconnect
	// cycles (which were re-firing this effect via `chat` identity
	// changes) and unrelated re-renders don't spam the broker with
	// duplicate read receipts. Only publish when the watermark
	// actually moves forward.
	const activeConversationId = useChatStore((s) => s.activeConversationId);
	const clearUnread = useChatStore((s) => s.clearUnread);
	const lastSentReadByConvoRef = useRef<Map<number, number>>(new Map());
	useEffect(() => {
		if (activeConversationId == null) return;
		clearUnread(activeConversationId);
		if (!chat.connected || principalId == null || outboxTopic == null) return;
		const msgs =
			useChatStore.getState().messagesByConversation[activeConversationId] ??
			[];
		const latestId = msgs.reduce((acc, m) => (m.id > acc ? m.id : acc), 0);
		if (latestId <= 0) return;
		const lastSent =
			lastSentReadByConvoRef.current.get(activeConversationId) ?? 0;
		if (latestId <= lastSent) return;
		lastSentReadByConvoRef.current.set(activeConversationId, latestId);
		chat
			.publish(outboxTopic, {
				type: 'read',
				clientMessageId: crypto.randomUUID(),
				conversationId: activeConversationId,
				messageId: latestId,
			})
			.catch(() => {
				/* read receipts are advisory */
			});
		// `chat` (the connection object) is intentionally excluded
		// from deps — its identity changes on every connect cycle,
		// which would refire this effect uselessly. We only want to
		// publish when the active convo or the connected state
		// transitions (and the watermark gates duplicates).
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [activeConversationId, chat.connected, principalId, clearUnread]);

	const loadConversationInto = React.useCallback(
		async (conversationId: number): Promise<void> => {
			try {
				const [detail, members] = await Promise.all([
					ChatQueryService.getConversation(conversationId),
					ChatQueryService.listMembers(conversationId),
				]);
				upsertConversation(detail.conversation);
				setParticipants(conversationId, members);
			} catch (err) {
				console.error('[ChatProvider] loadConversationInto failed', err);
			}
		},
		[upsertConversation, setParticipants],
	);

	const value = useMemo<ChatContextValue>(
		() => ({
			async sendText(conversationId, text, attachment) {
				if (principalId == null || outboxTopic == null) return;
				const clientMessageId = crypto.randomUUID();

				// Stamp the sender's workdUserId on the optimistic row. That's
				// the identity primitive the renderer uses for "is this
				// mine?" — a single numeric compare against the current
				// principal's workdUserId, no participants-list lookup
				// needed. This is what lets the bubble land on the right
				// side immediately, even on a freshly-opened conversation
				// where the participants fetch hasn't returned yet.
				// `senderParticipantId` is intentionally left undefined
				// here: only the server knows the conversation-scoped
				// participant id, and the fanout reply fills it in via
				// clientMessageId dedup (used by the OTHER side's
				// display-name lookup, never for alignment).
				const me = parsePrincipalId(principalId);

				appendMessage(conversationId, {
					id: -Date.now(),
					uuid: clientMessageId,
					conversationId,
					senderWorkdUserId: me?.workdUserId,
					clientMessageId,
					kind: attachment != null ? 'file' : 'text',
					content: text,
					createdDate: new Date().toISOString(),
					fkFileId: attachment?.fileId,
					fileName: attachment?.fileName,
					fileContentType: attachment?.contentType,
				});

				try {
					await chat.publish(outboxTopic, {
						type: 'message',
						clientMessageId,
						conversationId,
						text,
						fkFileId: attachment?.fileId,
					});
				} catch (err) {
					const msg =
						err instanceof Error ? err.message : String(err ?? 'Send failed');
					useChatStore.getState().setSendError(clientMessageId, msg);
					console.error('[ChatProvider] sendText failed', err);
				}
			},
			async retrySend(conversationId, clientMessageId) {
				if (principalId == null || outboxTopic == null) return;
				const state = useChatStore.getState();
				const stored = (
					state.messagesByConversation[conversationId] ?? []
				).find((m) => m.clientMessageId === clientMessageId);
				if (stored == null || stored.content == null) return;

				state.clearSendError(clientMessageId);
				try {
					await chat.publish(outboxTopic, {
						type: 'message',
						clientMessageId,
						conversationId,
						text: stored.content,
					});
				} catch (err) {
					const msg =
						err instanceof Error ? err.message : String(err ?? 'Send failed');
					useChatStore.getState().setSendError(clientMessageId, msg);
				}
			},
			async sendTyping(conversationId, active) {
				if (principalId == null || outboxTopic == null) return;
				// Swallow errors — typing is ephemeral and fires on every
				// keystroke; a pre-connect race is expected and harmless.
				try {
					await chat.publish(outboxTopic, {
						type: 'typing',
						clientMessageId: crypto.randomUUID(),
						conversationId,
						active,
					});
				} catch {
					/* noop */
				}
			},
			async sendRead(conversationId, messageId) {
				if (principalId == null || outboxTopic == null) return;
				try {
					await chat.publish(outboxTopic, {
						type: 'read',
						clientMessageId: crypto.randomUUID(),
						conversationId,
						messageId,
					});
				} catch {
					/* noop */
				}
			},
			loadConversationInto,
			retryConnection: chat.retry,
		}),
		[chat, principalId, outboxTopic, appendMessage, loadConversationInto],
	);

	return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
};

export const useChat = (): ChatContextValue => {
	const ctx = useContext(ChatContext);
	if (ctx == null) {
		throw new Error('useChat must be called inside <ChatProvider>');
	}
	return ctx;
};
